import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid2,
  Typography,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useBackdrop } from "../../../contexts/BackDrop.context";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import MUIErrorText from "../../../components/MUIErrorText/MUIErrorText";
import AppInputField from "../../../components/CommonReusableComponent/AppInputField/AppInputField";
import MUITableSearch from "../../../components/MUITable/MUITableSearch";
import { handleOutSideDataTableSearch } from "../../../common/MuiTableFunctions.config";
import { capitalizeFirstLetterOfEachWord, generateRandomId, setRequiredDataForDropdown } from "../../../common/CommonFunctions";
import { Icons } from "../../../config/MuiIcons.config";
import { AppTheme } from "../../../theme/App.theme";
import {
  APP_MESSAGE,
  ERROR_MESSAGE,
  FORM_VALIDATIONS,
  SNACKBAR_VARIANT,
  USE_FORMIK_SCHEMA,
} from "../../../config/Messages.config";
import {
  createAuditType,
  updateAuditType,
  getListOfAuditTypeCategories,
} from "../../../helpers/api-functions-helper";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getUserRoleAndScreens } from "../../../services/authServices/auth.services";
import deleteIcon from "../../../assets/images/Delete.svg";
import AppAutoCompleteField from "../../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";
import AuditTypeBulkUploadModal from "./AuditTypeBulkUploadModal";

const CreateOrUpdateAuditTypeMaster = () => {
  const params = useParams();
  const activityContainerRef = useRef(null);
  const { accountId, userUid } = getUserRoleAndScreens();
  const isViewMode = document.location.pathname.includes("/view");
  document.title = "Create Work Permit Type | Work Permit";
  const navigate = useNavigate();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { showSnackbar } = useSnackbar();
  const [dataThroughLocationState, setDataThroughLocationState] = useState({});
  const [auditTypeCategories, setAuditTypeCategories] = useState([]);

  // Bulk upload modal states
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);

  // Function to open bulk upload modal
  const handleBulkUploadModal = () => {
    setIsBulkUploadModalOpen(prev => !prev);
  }

  // States for check list
  const [searchText, setSearchText] =
    useState("");
  // const [isActivityListLoaded, setIsActivityListLoaded] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [activityListInstance, setActivityListInstance] =
    useState([]);

  // Error State
  const [errorState, setErrorState] = useState({
    activityList: "",
    error: {}
  });

  const countDivStyle = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: AppTheme.PRIMARY_COLOR_LIGHT,
  };

  const styleForActiviListDiv = {
    parent: {
      height: "50vh",
      overflowY: "auto",
    },
    child: {
      backgroundColor: AppTheme.PRIMARY_COLOR_LIGHT,
      borderRadius: "5px",
    },
    activityDeleteIcon: {
      width: "35px",
      height: "35px",
      background: "white",
      display: isViewMode && params?.auditTypeId ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  };

  const footerButtonStyle = {
    display: "flex",
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    backgroundColor: "white",
  };

  const scrollDownActivityDiv = () => {
    setTimeout(() => {
      if (activityContainerRef.current) {
        activityContainerRef.current.scrollTop =
          activityContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  useEffect(() => {
    const auditTypeDetails = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_DEV_AUDIT_TYPE_DETAILS)
    );
    if (auditTypeDetails !== null && auditTypeDetails !== undefined) {
      setDataThroughLocationState({ ...auditTypeDetails });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to Create Work Permit Type
  const createNewAuditType = (Data) => {
    showBackdrop();
    createAuditType(Data)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, success, data, message } = response.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data || message || APP_MESSAGE.AUDIT_TYPE_MASTER.CREATE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.AUDIT_TYPE_MASTER.CREATE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            navigate(-1);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.AUDIT_TYPE_MASTER.CREATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  // Function to Create Work Permit Type
  const updateExistingAuditType = (Data) => {
    showBackdrop();
    updateAuditType(Data)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, success, data, message } = response.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data || message || APP_MESSAGE.AUDIT_TYPE_MASTER.UPDATE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.AUDIT_TYPE_MASTER.UPDATE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            localStorage.removeItem(
              process.env.REACT_APP_DEV_AUDIT_TYPE_DETAILS
            );
            navigate(-1);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.AUDIT_TYPE_MASTER.UPDATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  const fetchAllAuditTypeCategories = async () => {
    showBackdrop();
    try {
      const categoryRes = await getListOfAuditTypeCategories(accountId);
      const { statusCode, data, message } = categoryRes?.data;
      if (statusCode && statusCode === 200) {
        setRequiredDataForDropdown(setAuditTypeCategories, data, "name", "id")
      } else {
        showSnackbar(
          data || message || ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      }
    } catch (error) {
      console.log("Error: ", error);
      showSnackbar(ERROR_MESSAGE.FAILED_TO_FETCH_DATA, SNACKBAR_VARIANT.ERROR);
    } finally {
      hideBackdrop();
    }
  };

  const checkValidations = async (CHECK_LIST) => {
    if (!CHECK_LIST[0]) {
      return {
        activityList: "Please create at least one activity",
      };
    } else {
      return false;
    }
  };

  const handleAuditTypeCreationOrUpdation = async (Data) => {
    const final_activity_list = activityListInstance.map(
      ({ id, ...activity }) => activity
    );

    const isValid = await checkValidations(final_activity_list);

    if (!isValid) {
      if (params && params.auditTypeId) {
        // const { abbreviation, ...rest } = Data;
        const payload_for_update = {
          ...Data,
          id: params.auditTypeId,
          activityList: [...final_activity_list],
          accountId: accountId,
          updatedBy: userUid,
        };
        updateExistingAuditType(payload_for_update);
        // console.log("payload_for_update: ", payload_for_update);
      } else {
        const payload_for_create = {
          ...Data,
          abbreviation: Data?.abbreviation.trim(),
          name: capitalizeFirstLetterOfEachWord(Data?.name).trim(),
          activityList: [...final_activity_list],
          accountId: accountId,
          createdBy: userUid,
          updatedBy: userUid,
        };
        createNewAuditType(payload_for_create);
        // console.log("payload_for_create: ", payload_for_create);
      }
    } else {
      setErrorState({
        activityList: isValid?.activityList
      })
    }
  };

  // Function to reset form
  const handleFormReset = () => {
    setActivityList([]);
    setActivityListInstance([]);
  };

  // Handle update data through bulk upload
  const handleBulkActivitiesUpdate = (data = [], keepExistingActivities) => {
    if (data && data.length > 0) {
      const revisedActivityList = data.map((activity) => {
        return {
          id: generateRandomId(15),
          name: activity,
        };
      });
      if (keepExistingActivities) {
        setActivityListInstance((prev) => [...prev, ...revisedActivityList]);
        setActivityList((prev) => [...prev, ...revisedActivityList]);
      } else {
        setActivityListInstance(revisedActivityList);
        setActivityList(revisedActivityList);
      }
    } else {
      showSnackbar(
        "No activities found in the uploaded file.",
        SNACKBAR_VARIANT.ERROR
      );
    }
  }

  // Formik form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (params.auditTypeId && dataThroughLocationState?.name) || "",
      abbreviation:
        (params.auditTypeId && dataThroughLocationState?.abbreviation) || "",
      auditTypeCategoryId: 
        (params.auditTypeId && dataThroughLocationState?.auditTypeCategoryId) || ""  
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        // .matches(
        //   FORM_VALIDATIONS.TEXT_INPUT.REGEX.INITIAL,
        //   FORM_VALIDATIONS.TEXT_INPUT.MESAAGE.INITIAL
        // )
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_MASTER.AUDIT_NAME),
      abbreviation: Yup.string()
        .trim()
        .matches(
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.REGEX,
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.MESSAGE
        )
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_MASTER.ABBRIVATIONS),
      auditTypeCategoryId: Yup.string()
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_MASTER.AUDIT_TYPE_CATEGORY),
    }),
    onSubmit: (values) => {
      if (Object.keys(errorState.error).length > 0) {
        showSnackbar(
          "Activity fields cannot be left blank, please enter valid data.",
          SNACKBAR_VARIANT.ERROR
        );
      } else {
        handleAuditTypeCreationOrUpdation(values);
      }
    },
    onReset: () => {
      handleFormReset();
    },
  });

  useEffect(() => {
    fetchAllAuditTypeCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Search functionality for checklist
  useEffect(() => {
    if (searchText === "") {
      // Reset to original data if search text is cleared
      setActivityList(activityListInstance);
    } else {
      const filteredData_ = handleOutSideDataTableSearch(
        searchText,
        activityListInstance
      );
      setActivityList(filteredData_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);


  useEffect(() => {
    if (dataThroughLocationState && dataThroughLocationState?.id) {
      const revisedActivityList = dataThroughLocationState?.activityList.map(
        (activity) => {
          return {
            id: generateRandomId(),
            name: activity.name,
          };
        }
      );
      setActivityListInstance(revisedActivityList)
      setActivityList(revisedActivityList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataThroughLocationState]);

  // Detect errors and scroll to top if there are any
  useEffect(() => {
    if (Object.keys(validation.errors).length > 0 && validation.isSubmitting) {
      window.scroll({ top: 0, behavior: "smooth" }); // Scroll to top smoothly
    }
  }, [validation]);

  const activityActions = {
    add: "ADD",
    update: "UPDATE",
    delete: "DELETE",
  }

  const handleActivityUpdate = (action, Id = null, Value = "") => {
    let updatedData = [...activityListInstance];
    let newErrors = { ...errorState.error };

    switch (action) {
      case activityActions.add:
        const newId = generateRandomId(15);
        updatedData.push({
          name: "",
          id: newId,
        });
        newErrors[newId] = "This field cannot be empty.";
        scrollDownActivityDiv();
        break;

      case activityActions.update:
        updatedData = updatedData.map((item) => {
          if (item.id === Id) {
            // If value is empty, add error; otherwise, remove error
            if (Value.trim() === "") {
              newErrors[Id] = "This field cannot be empty.";
            } else {
              delete newErrors[Id];
            }

            return { ...item, name: Value };
          } else {
            return item;
          }
        });
        break;

      case activityActions.delete:
        updatedData = updatedData.filter((item) => item.id !== Id);
        // Remove error if the field is deleted
        delete newErrors[Id];
        break;

      default:
        return;
    }

    setActivityList(updatedData);
    setActivityListInstance(updatedData)
    setErrorState((prev) => ({
      ...prev,
      error: newErrors
    }))
  };


  return (
    <React.Fragment>
      <AuditTypeBulkUploadModal
        ModalState={isBulkUploadModalOpen}
        UpdateParentData={(data, keepExistingActivities) => {
          handleBulkActivitiesUpdate(data, keepExistingActivities);
        }}
        ToggleFunction={handleBulkUploadModal}
        ModalType="UPDATE"
      />
      <div
        className="page-content"
        style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <Grid2 container className="mb-2">
          <Grid2 size={12}>
            <div className="d-flex align-items-center gap-2 py-1">
              <Icons.KeyboardBackspaceIcon
                className="cursor-pointer"
                style={{ color: "GrayText" }}
                onClick={() => {
                  localStorage.removeItem(
                    process.env.REACT_APP_DEV_AUDIT_TYPE_DETAILS
                  );
                  navigate(-1);
                }}
              />
              <Typography className="inter-medium-500">
                {`${
                  params.auditTypeId && !isViewMode
                    ? "Update"
                    : isViewMode
                    ? "View"
                    : "Create"
                } Audit Type`}
              </Typography>
            </div>
          </Grid2>
        </Grid2>
        <Divider
          className="mb-1"
          style={{ color: "GrayText", border: "0.02rem solid GrayText" }}
        />
        <Grid2 container spacing={2} className="mb-2">
          <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
            <Typography variant="body2" className="inter-regular-400 py-2">
              Audit Type Name
            </Typography>
            <AppInputField
              Name={"name"}
              Id={"name"}
              Type={"text"}
              Disabled={isViewMode || (params?.auditTypeId ? true : false)}
              Value={validation.values.name || ""}
              OnBlur={validation.handleBlur}
              IsCapitalizeRequired={true}
              OnChange={(e) => {
                validation.handleChange(e);
              }}
              PlaceHolder={"Enter name"}
              Error={
                validation.touched.name && validation.errors.name ? true : false
              }
              HelperText={
                validation.touched.name && validation.errors.name ? (
                  <MUIErrorText ErrorMessage={validation.errors.name} />
                ) : null
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 3 }}>
            <Typography variant="body2" className="inter-regular-400 py-2">
              Audit Type Category
            </Typography>
            <AppAutoCompleteField
              TextInputName={"auditTypeCategoryId"}
              TextInputId={"auditTypeCategoryId"}
              Id={"auditTypeCategoryId"}
              TextInputPlaceholder={"Select audit type category"}
              IsMultiple={false}
              Options={auditTypeCategories}
              Disabled={isViewMode}
              SortOptions={true}
              HandleOnBlur={validation.handleBlur}
              HandleOnChange={(event, obj) => {
                const ID = (obj && obj?.value) || "";
                const ev = {
                  ...event,
                  target: {
                    ...event?.target,
                    name: "auditTypeCategoryId",
                    value: ID,
                  },
                };
                validation.handleChange(ev);
              }}
              Value={validation.values.auditTypeCategoryId}
              TextInputError={
                validation.touched.auditTypeCategoryId &&
                validation.errors.auditTypeCategoryId
                  ? true
                  : false
              }
              TextInputHelperText={
                validation.touched.auditTypeCategoryId &&
                validation.errors.auditTypeCategoryId
                  ? validation.errors.auditTypeCategoryId
                  : null
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 2 }}>
            <Typography variant="body2" className="inter-regular-400 py-2">
              Unique Prefix
            </Typography>
            <AppInputField
              Name={"abbreviation"}
              Id={"abbreviation"}
              Type={"text"}
              Disabled={isViewMode || (params?.auditTypeId ? true : false)}
              Value={validation.values.abbreviation || ""}
              OnBlur={validation.handleBlur}
              OnChange={(event) => {
                const { value, name } = event.target;
                validation.handleChange({
                  ...event,
                  target: {
                    ...event.target,
                    name: name,
                    value: value.toUpperCase(),
                  },
                });
              }}
              PlaceHolder={"Enter unique prefix"}
              Error={
                validation.touched.abbreviation &&
                validation.errors.abbreviation
                  ? true
                  : false
              }
              HelperText={
                validation.touched.abbreviation &&
                validation.errors.abbreviation ? (
                  <MUIErrorText ErrorMessage={validation.errors.abbreviation} />
                ) : null
              }
            />
          </Grid2>
        </Grid2>
        <Grid2 container spacing={2} className="mt-2">
          <Grid2 size={12}>
            <Card>
              <CardContent className="py-2">
                <div className="d-flex align-items-center justify-content-between">
                  <Typography variant="body1" className="inter-medium-500">
                    Activities
                  </Typography>
                  <div className="d-flex align-items-center gap-3">
                    {isViewMode && params?.auditTypeId ? null : (
                      <div>
                        {/* <AppButton
                          children={"Upload Bulk Activities"}
                          Size={"small"}
                          ClassName={"px-3"}
                          OnPress={handleBulkUploadModal}
                        /> */}
                        <Tooltip
                          title={"Upload Bulk Activities"}
                          placement="top"
                        >
                          <IconButton onClick={handleBulkUploadModal}
                            style={{
                              backgroundColor: AppTheme.PRIMARY_COLOR,
                              color: AppTheme.WHITE,
                            }}
                          >
                            <Icons.UploadIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    <div
                      style={{
                        display:
                          isViewMode && params?.auditTypeId
                            ? "none"
                            : "initial",
                      }}
                    >
                      <AppButton
                        children={"Add Activity"}
                        Size={"small"}
                        ClassName={"px-3"}
                        OnPress={() => {
                          handleActivityUpdate(activityActions.add);
                        }}
                      />
                    </div>
                    <div style={countDivStyle}>
                      {activityListInstance?.length}
                    </div>
                  </div>
                </div>
                {errorState.activityList !== "" && (
                  <div className="mb-1">
                    <MUIErrorText ErrorMessage={errorState.activityList} />
                  </div>
                )}
                {!isViewMode && (
                  <div className="mt-2">
                    <MUITableSearch
                      CancelSearch={() => {
                        setSearchText("");
                      }}
                      HandleSearch={(e) => setSearchText(e.target.value)}
                      SearchText={searchText}
                    />
                  </div>
                )}
                <div
                  ref={activityContainerRef}
                  style={styleForActiviListDiv.parent}
                  className="mt-2 activity-container"
                >
                  {activityList[0] ? (
                    activityList.map((activity) => (
                      <div
                        key={activity?.id}
                        className="my-2 py-2 px-3"
                        style={styleForActiviListDiv.child}
                      >
                        <Typography
                          variant="body2"
                          className="inter-regular-400 pt-2"
                        >
                          Enter Activity
                        </Typography>
                        <div className="d-flex align-items-center gap-2 my-2">
                          <AppInputField
                            TextFieldSX={{
                              backgroundColor: "#fff",
                            }}
                            IsMultiLine={true}
                            MaxRows={5}
                            Disabled={isViewMode}
                            key={activity?.id}
                            Value={activity.name}
                            OnChange={(event) => {
                              const { value } = event?.target;
                              handleActivityUpdate(
                                activityActions.update,
                                activity?.id,
                                value
                              );
                            }}
                            Error={!!errorState.error[activity?.id]}
                          />
                          <div
                            className="box-elevation-1 p-1"
                            style={styleForActiviListDiv.activityDeleteIcon}
                          >
                            <img
                              src={deleteIcon}
                              className="cursor-pointer"
                              alt={"Delete"}
                              style={{ width: "22px", height: "22px" }}
                              onClick={() => {
                                handleActivityUpdate(
                                  activityActions.delete,
                                  activity?.id
                                );
                              }}
                            />
                          </div>
                        </div>
                        {errorState.error[activity?.id] ? (
                          <MUIErrorText
                            ErrorMessage={errorState.error[activity?.id]}
                          />
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div>
                      <Typography
                        variant="body2"
                        className="inter-regular-400 mt-3"
                        align="center"
                      >
                        {activityListInstance[0]
                          ? "There are no records to display"
                          : "No activities added yet."}
                      </Typography>
                    </div>
                  )}
                  {/* {isActivityListLoaded ? (
                    <MUILoader />
                  ) : (
                    <>
                      {activityListTableData[0] ? (
                        activityListTableData.map((activity_list, id) => (
                          <div
                            key={id}
                            className="m-2 p-1"
                            style={styleForActiviListDiv.child}
                          >
                            <FormControlLabel
                              className="ps-2"
                              style={{
                                height: "fit-content",
                              }}
                              control={
                                <MuiCheckBox
                                  Disabled={isViewMode}
                                  OnChange={(event) =>
                                    handleCheckboxChangeForActivityList(
                                      event,
                                      activity_list
                                    )
                                  }
                                  Value={activity_list.name}
                                  Checked={activity_list.selected}
                                />
                              }
                              label={
                                <Typography
                                  variant="body2"
                                  className="inter-regular-400"
                                >
                                  {activity_list.name}
                                </Typography>
                              }
                            />
                          </div>
                        ))
                      ) : (
                        <div>
                          <Typography
                            variant="body2"
                            className="inter-regular-400 mt-3"
                            align="center"
                          >
                            There are no records to display.
                          </Typography>
                        </div>
                      )}
                    </>
                  )} */}
                </div>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {!isViewMode ? (
          <div style={footerButtonStyle}>
            <Grid2 container spacing={2} className="mt-2 pb-3 w-100">
              {!params.auditTypeId && !isViewMode ? (
                <>
                  <Grid2 size={6}>
                    <AppButton
                      Type={"reset"}
                      OnPress={validation.handleReset}
                      children={"Reset"}
                      Color={"error"}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <AppButton
                      Type={"submit"}
                      OnPress={() => {
                        validation.handleSubmit();
                      }}
                      children={"Create"}
                    />
                  </Grid2>
                </>
              ) : params.auditTypeId && !isViewMode ? (
                <Grid2 size={12}>
                  <AppButton
                    Type={"submit"}
                    OnPress={() => {
                      validation.handleSubmit();
                    }}
                    children={"Update"}
                  />
                </Grid2>
              ) : null}
            </Grid2>
          </div>
        ) : null}
      </div>
    </React.Fragment>
  );
};

export default CreateOrUpdateAuditTypeMaster;
